import {
  del,
  get,
  axiosConfig,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type { GalleryItem, GalleryPayload } from "@project/types/gallery";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  unwrapItem,
} from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/gallery`;
const SILENT: RequestConfig = { showNotification: false };

function toFormData(payload: Partial<GalleryPayload>): FormData {
  const fd = new FormData();
  if (payload.title !== undefined) {
    fd.append("title", payload.title);
  }
  if (
    payload.category !== undefined &&
    payload.category !== null &&
    payload.category !== ""
  ) {
    fd.append("category", String(payload.category));
  }
  if (payload.order_by !== undefined && payload.order_by !== null) {
    fd.append("order_by", String(payload.order_by));
  }
  if (payload.active !== undefined) {
    fd.append("active", String(payload.active));
  }
  if (payload.image instanceof File) {
    fd.append("image", payload.image);
  }
  return fd;
}

class GalleryService {
  listPage = (query: ListQuery = {}): Promise<PaginatedResult<GalleryItem>> =>
    fetchListPage<GalleryItem>(`${BASE}/`, query);

  get = async (id: number): Promise<GalleryItem | undefined> => {
    const data = await get<ApiItemResponse<GalleryItem> | GalleryItem>(
      `${BASE}/${id}/`,
      SILENT_LIST
    );
    return unwrapItem(data);
  };

  create = async (payload: GalleryPayload): Promise<GalleryItem> => {
    const fd = toFormData(payload);
    const response = await axiosConfig.post<
      ApiItemResponse<GalleryItem> | GalleryItem
    >(`${BASE}/`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to create gallery item.");
    }
    return item;
  };

  update = async (
    id: number,
    payload: Partial<GalleryPayload>
  ): Promise<GalleryItem | undefined> => {
    const fd = toFormData(payload);
    const response = await axiosConfig.patch<
      ApiItemResponse<GalleryItem> | GalleryItem
    >(`${BASE}/${id}/`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return unwrapItem(response.data);
  };

  delete = async (id: number): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };
}

export const galleryService = new GalleryService();
